<template>
  <div class="product-groups" v-loading="loading">
    <div class="product-groups-hint">
      O mesmo item com outro ID, como um cadastro duplicado. Quem já recebeu um deles não recebe os
      outros.
    </div>

    <snowflake-remote-search-select v-if="editable" object-value clear-on-select
      :search-function="productService.search" :find-function="productService.getProductById"
      :label-function="productLabel" placeholder="Adicionar produto idêntico" @change="add">
    </snowflake-remote-search-select>

    <div class="product-group">
      <el-tag v-for="product in products" :key="product.id" :closable="editable" disable-transitions
        @close="remove(product)">{{ productLabel(product) }}</el-tag>
      <div v-if="products.length === 0" class="no-products">Nenhum produto idêntico</div>
    </div>
  </div>
</template>

<script>
import ProductService from '@/services/stock/ProductService';
import SnowflakeRemoteSearchSelect from '@/components/input/RemoteSearchSelect.vue';

// Edits the products identical to the product; the changes are sent by save, when the product is
// saved
export default {
  name: 'product-identical-products-form',
  components: {
    SnowflakeRemoteSearchSelect,
  },
  props: {
    productId: {
      type: Number,
      required: true,
    },
    editable: Boolean,
  },
  data() {
    return {
      loading: false,
      products: [],
      productService: ProductService,
    };
  },
  async created() {
    this.loading = true;

    try {
      this.setGroups(await ProductService.findIdenticalGroups(this.productId));
    } catch (error) {
      this.$message.error(`Não foi possível carregar os produtos idênticos: ${error.message}`);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async save() {
      const productIds = this.products.map(product => product.id);

      this.setGroups(await ProductService.updateIdenticalGroup(this.productId, productIds));
    },
    // Every product in the groups but this one; groups set before the product form may be several
    setGroups(groups) {
      this.products = this.othersIn(groups, []);
    },
    othersIn(groups, listed) {
      const others = [];

      groups.flatMap(group => group.products).forEach((product) => {
        const known = [...listed, ...others].some(other => other.id === product.id);

        if (product.id !== this.productId && !known) others.push(product);
      });

      return others;
    },
    // The product comes with the ones identical to it, as all are the same item
    async add(product) {
      if (!product) return;

      if (product.id === this.productId) {
        this.$message.warning('Este é o próprio produto.');

        return;
      }

      if (this.products.some(other => other.id === product.id)) return;

      let groups;

      try {
        groups = await ProductService.findIdenticalGroups(product.id);
      } catch (error) {
        this.$message.error(
          `Não foi possível carregar os idênticos de ${product.name}: ${error.message}`);

        return;
      }

      const added = { id: product.id, name: product.name };
      const alongside = this.othersIn(groups, [...this.products, added]);

      this.products.push(added, ...alongside);

      if (alongside.length > 0) {
        const names = alongside.map(other => this.productLabel(other)).join(', ');

        this.$message.info(`Também entraram, por serem idênticos a ${product.name}: ${names}.`);
      }
    },
    remove(product) {
      this.products = this.products.filter(other => other.id !== product.id);
    },
    // A product deleted since it joined the group has no name
    productLabel(product) {
      return product.name ? `${product.name} (#${product.id})` : `#${product.id} (excluído)`;
    },
  },
};
</script>

<style lang="scss" scoped>
div.product-groups {
  text-align: left;
  line-height: normal;
}

div.product-groups-hint {
  margin-bottom: 8px;
  font-size: 12px;
  color: #5e7382;
}

div.product-group {
  border: #c1c1c1 dashed 1.5px;
  border-radius: 4px;
  margin-top: 8px;
  padding: 8px 12px;

  .el-tag {
    margin: 4px;
  }
}

div.no-products {
  text-align: center;
  font-size: 12px;
  color: #5e7382;
}
</style>
